"use client";

import { useState, useMemo } from "react";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { PRICING_CONFIG, ANNUAL_PRICING } from "@/lib/pricing-config";

const ENTERPRISE_FLOOR_MONTHLY = 250_000;

type TierKey = "starter" | "pro" | "enterprise";

function resolveTier(properties: number): TierKey {
  if (properties <= 1) return "starter";
  if (properties <= 3) return "pro";
  return "enterprise";
}

const formatNumber = (value: number, locale: string = "ru-RU"): string =>
  new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(
    Math.round(value),
  );

export function RoiCalculator() {
  const { t, locale } = useI18n();
  const isEn = locale === "en";

  const [properties, setProperties] = useState(3);
  const [currentDays, setCurrentDays] = useState(5);

  const calc = useMemo(() => {
    const savedDaysPerMonth = currentDays * 0.6;
    const hoursPerMonth = properties * currentDays * 8 * 0.6;
    const grossAnnual = Math.round(hoursPerMonth) * 1_500 * 12;

    const tierKey = resolveTier(properties);
    const isEnterprise = tierKey === "enterprise";

    const monthlySubAnnual = isEnterprise
      ? ENTERPRISE_FLOOR_MONTHLY * 12
      : PRICING_CONFIG[tierKey].rub * 12;

    const annualSubAnnual = isEnterprise
      ? ENTERPRISE_FLOOR_MONTHLY * 12
      : ANNUAL_PRICING[tierKey].rubPerYear;

    const tierDisplayName = tierKey === "starter" ? "Starter" : tierKey === "pro" ? "Pro" : "Enterprise";

    return {
      savedDaysPerMonth: Math.round(savedDaysPerMonth * 10) / 10,
      hoursPerMonth: Math.round(hoursPerMonth),
      grossAnnual,
      tierKey,
      tierDisplayName,
      isEnterprise,
      monthlySubAnnual,
      annualSubAnnual,
      netMonthlyBilling: Math.max(0, grossAnnual - monthlySubAnnual),
      netAnnualBilling: Math.max(0, grossAnnual - annualSubAnnual),
    };
  }, [properties, currentDays]);

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-5xl mx-auto">
        <div className="mb-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-keiront-obsidian mb-4">
            {t("landing.roi.title")}
          </h2>
          <p className="text-lg text-slate-600">{t("landing.roi.subtitle")}</p>
        </div>

        <div className="bg-slate-50/50 rounded-2xl p-8 sm:p-12 mb-8 border border-slate-200">
          {/* Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12 max-w-2xl">
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-keiront-obsidian mb-3">
                {t("landing.roi.input1Label")}
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={properties}
                onChange={(e) => setProperties(Math.max(1, parseInt(e.target.value) || 1))}
                className="px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-keiro-green focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-2">{t("landing.roi.input1Hint")}</p>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-semibold text-keiront-obsidian mb-3">
                {t("landing.roi.input3Label")}
              </label>
              <input
                type="number"
                min="1"
                max="30"
                value={currentDays}
                onChange={(e) => setCurrentDays(Math.max(1, parseInt(e.target.value) || 1))}
                className="px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-keiro-green focus:border-transparent"
              />
              <p className="text-xs text-slate-500 mt-2">{t("landing.roi.input3Hint")}</p>
            </div>
          </div>

          {/* Output cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {t("landing.roi.output1Label")}
              </p>
              <div className="text-3xl font-bold text-keiront-obsidian">
                {formatNumber(calc.savedDaysPerMonth, locale)}
              </div>
              <p className="text-sm text-slate-600 mt-1">{t("landing.roi.output1Unit")}</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {t("landing.roi.output2Label")}
              </p>
              <div className="text-3xl font-bold text-keiront-obsidian">
                {formatNumber(calc.hoursPerMonth, locale)}
              </div>
              <p className="text-sm text-slate-600 mt-1">{t("landing.roi.output2Unit")}</p>
            </div>

            {!isEn ? (
              <>
                <div className="bg-white rounded-xl p-6 border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    {t("landing.roi.output3Label")}
                  </p>
                  <div className="text-3xl font-bold text-keiront-obsidian">
                    {formatNumber(calc.grossAnnual, locale)}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{t("landing.roi.output3Unit")}</p>
                </div>

                <div className="bg-keiront-green rounded-xl p-6 border border-keiront-dark">
                  <p className="text-xs font-semibold text-white/80 uppercase tracking-wider mb-2">
                    {calc.netAnnualBilling > 0
                      ? t("landing.roi.output4Label")
                      : t("landing.roi.output4FallbackLabel")}
                  </p>
                  <div className="text-3xl font-bold text-white">
                    {formatNumber(calc.netAnnualBilling > 0 ? calc.netAnnualBilling : calc.grossAnnual, locale)}
                  </div>
                  <p className="text-sm text-white/90 mt-1">
                    {calc.netAnnualBilling > 0
                      ? t("landing.roi.output4Unit")
                      : t("landing.roi.output4FallbackUnit")}
                  </p>
                </div>
              </>
            ) : (
              <div className="bg-keiront-green rounded-xl p-6 border border-keiront-dark md:col-span-2 flex flex-col justify-center">
                <p className="text-sm text-white/90 leading-relaxed">
                  We&apos;ll translate the time saved into annual savings in your
                  currency on a 30-minute demo.
                </p>
                <a
                  href="#demo"
                  className="mt-3 inline-flex items-center gap-2 text-white font-semibold underline underline-offset-4"
                >
                  Book a demo →
                </a>
              </div>
            )}
          </div>

          {/* Billing comparison — RU only (рублёвая раскладка) */}
          {!isEn && (
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="text-sm font-semibold text-slate-700">
                {t("landing.roi.comparisonTier")}:
              </span>
              <span className="rounded-full bg-keiront-green/10 px-3 py-0.5 text-sm font-bold text-keiront-green">
                {calc.tierDisplayName}
              </span>
              {calc.isEnterprise && (
                <span className="text-sm text-slate-500">
                  {t("landing.roi.comparisonEnterpriseNote")}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Monthly billing */}
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  {t("landing.roi.comparisonMonthly")}
                </p>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-500">{t("landing.roi.comparisonSubscription")}</span>
                  <span className="font-medium text-slate-700">
                    −₽{formatNumber(calc.monthlySubAnnual, locale)}
                  </span>
                </div>
                <div className="flex justify-between text-sm border-t border-slate-100 pt-2">
                  <span className="font-semibold text-slate-700">
                    {t("landing.roi.comparisonNetSavings")}
                  </span>
                  <span
                    className={`font-bold ${calc.netMonthlyBilling > 0 ? "text-slate-900" : "text-slate-400"}`}
                  >
                    {calc.netMonthlyBilling > 0
                      ? `₽${formatNumber(calc.netMonthlyBilling, locale)}`
                      : t("landing.roi.netZeroHint")}
                  </span>
                </div>
              </div>

              {/* Annual billing — highlighted */}
              <div className="rounded-lg border-2 border-keiront-green bg-keiro-mist/50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {t("landing.roi.comparisonAnnual")}
                  </p>
                  <span className="rounded-full bg-keiro-mist px-2 py-0.5 text-xs font-bold text-keiro-green-dark">
                    −25%
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-500">{t("landing.roi.comparisonSubscription")}</span>
                  <span className="font-medium text-slate-700">
                    −₽{formatNumber(calc.annualSubAnnual, locale)}
                  </span>
                </div>
                <div className="flex justify-between text-sm border-t border-keiro-mist pt-2">
                  <span className="font-semibold text-slate-700">
                    {t("landing.roi.comparisonNetSavings")}
                  </span>
                  <span
                    className={`font-bold ${calc.netAnnualBilling > 0 ? "text-keiront-green" : "text-slate-400"}`}
                  >
                    {calc.netAnnualBilling > 0
                      ? `₽${formatNumber(calc.netAnnualBilling, locale)}`
                      : t("landing.roi.netZeroHint")}
                  </span>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 italic">{t("landing.roi.disclaimer")}</p>

        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold text-slate-900 mb-2">{t("landing.roi.ctaTitle")}</h3>
          <p className="text-slate-600 mb-6">{t("landing.roi.ctaSubtitle")}</p>
          <a
            href="#demo"
            className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-keiront-green text-white font-semibold hover:bg-keiront-dark transition-colors"
          >
            {t("landing.roi.ctaButton")}
          </a>
        </div>
      </div>
    </section>
  );
}
